/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef } from 'react';
import { type ContextValue, type RadioProps as RadioBaseProps, Radio as RadioCore, type RadioRenderProps as RadioRenderPropsBase, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import type { StyleOrFunction } from '../../types';
import { applyStylesWithRenderProps } from '../../utils/apply-styles-with-render-props/apply-styles-with-render-props.util';
import { resolveComponentStyle } from '../../utils/resolve-component-style/resolve-component-style.util';
import { Label, type LabelProps } from '../label/label.component';

interface RadioProps extends Omit<RadioBaseProps, 'style' | 'className'> {
  /** The size variant of the radio. (Default: 20) */
  size?: keyof typeof sizeVariants;
  style?: StyleOrFunction<RadioRenderPropsBase> | {
    base?: StyleOrFunction<RadioRenderPropsBase>;
    label?: StyleOrFunction<RadioRenderPropsBase>;
  };
}

const RadioContext = createContext<ContextValue<RadioProps, HTMLLabelElement>>({});

const Radio = forwardRef((props: RadioProps, ref: ForwardedRef<HTMLLabelElement>) => {
  [props, ref] = useContextProps(props, ref, RadioContext);

  const { style, size = 20, children, ...coreProps } = props;

  return (
    <RadioCore
      ref={ref}
      {...applyStylesWithRenderProps<RadioRenderPropsBase>((state) => stylex.props(styles.base, sizeVariants[size], resolveComponentStyle(style, state, 'base')))}
      {...coreProps}>
      {(renderProps) => {
        const evaluatedChildren = typeof children === 'function' ? children(renderProps) : children;

        return evaluatedChildren && (
          <Label
            size={LABEL_SIZES[size]}
            style={[labelStyles.base, resolveComponentStyle(style, renderProps, 'label')]}
            color={(
              (renderProps.isDisabled && 'disabled') ||
              (renderProps.isSelected || renderProps.isHovered) && 'primary' ||
              'tertiary'
            )}>
            {evaluatedChildren}
          </Label>
        );
      }}
    </RadioCore>
  );
});

const LABEL_SIZES: Record<keyof typeof sizeVariants, LabelProps['size']> = {
  16: 14,
  20: 16,
  24: 18,
};

const styles = stylex.create({
  base: {
    alignItems: 'center',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'inline-flex',
    position: 'relative',
    userSelect: 'none',
    // Not recommended by @stylexjs/valid-styles but there is no other way to conditionally style the pseudo-element
    /* eslint-disable @stylexjs/valid-styles */
    ':is([data-hovered]):not([data-selected])': {
      '::before': {
        borderColor: $.borderContrast,
        borderWidth: 1.5,
      }
    },
    ':is([data-selected])': {
      '::before': {
        backgroundColor: $.iconWhite,
        borderColor: $.surfaceHighlight,
      }
    },
    ':is([data-focus-visible])': {
      '::before': {
        outlineColor: $.borderInteractive,
        outlineOffset: 2,
        outlineStyle: 'solid',
        outlineWidth: 2,
      }
    },
    ':is([data-disabled])': {
      '::before': {
        borderColor: $.border08,
      }
    },
    /* eslint-enable @stylexjs/valid-styles */
    '::before': {
      backgroundColor: 'inherit',
      borderColor: $.border20,
      borderRadius: '999px',
      borderStyle: 'solid',
      borderWidth: 1,
      boxShadow: $.shadowBottom020Silent,
      boxSizing: 'border-box',
      content: '',
      display: 'block',
      transitionDuration: '200ms',
      transitionProperty: 'border, outline-offset',
    },
  },
});

const sizeVariants = stylex.create({
  16: {
    gap: 8,
    // Not recommended by @stylexjs/valid-styles but there is no other way to conditionally style the pseudo-element
    /* eslint-disable @stylexjs/valid-styles */
    ':is([data-selected])': {
      '::before': {
        borderWidth: 5,
      }
    },
    /* eslint-enable @stylexjs/valid-styles */
    '::before': {
      height: 16,
      width: 16,
    },
  },
  20: {
    gap: 12,
    // Not recommended by @stylexjs/valid-styles but there is no other way to conditionally style the pseudo-element
    /* eslint-disable @stylexjs/valid-styles */
    ':is([data-selected])': {
      '::before': {
        borderWidth: 6,
      }
    },
    /* eslint-enable @stylexjs/valid-styles */
    '::before': {
      height: 20,
      width: 20,
    }
  },
  24: {
    gap: 16,
    // Not recommended by @stylexjs/valid-styles but there is no other way to conditionally style the pseudo-element
    /* eslint-disable @stylexjs/valid-styles */
    ':is([data-selected])': {
      '::before': {
        borderWidth: 7,
      }
    },
    /* eslint-enable @stylexjs/valid-styles */
    '::before': {
      height: 24,
      width: 24,
    }
  }
});

const labelStyles = stylex.create({
  base: {
    transitionDuration: '200ms',
    transitionProperty: 'color',
  }
});

const RADIO_SLOTS = {
  ERROR_MESSAGE: 'errorMessage',
  DESCRIPTION: 'description',
};

export { RADIO_SLOTS, Radio, RadioContext, type RadioProps };

